import { useCallback } from 'react';

import { Box, Flex, Text, useColorMode } from '@chakra-ui/react';
import { captureException } from '@sentry/nextjs';
import template from 'lodash/template';
import { usePathname } from 'next/navigation';

import Link from '@/components/Link';
import {
  SENTRY_TAGS,
  SENTRY_TAGS_TYPE_VALUES,
} from '@/constants/monitoring.constants';
import { IIndexable, MainMenuItem, SubMenuType } from '@/customtypes/common';
import BaseImage from '@/dapp-sdk-v2/components/Image';
import { env } from '@/env';
import { TextPrimary } from '@/themes';
import { getCurrentChain } from '@/utils/common';

interface OptionsProps {
  activeMenuKey: string;
  sidebarData: Omit<MainMenuItem, 'icon'>;
  onClick(key: string): void;
  title: string;
  isEmbed: boolean;
}

const IconMappings: IIndexable = {
  ETH: {
    light: '/assets/images/sidebar/eth_light.svg',
    dark: '/assets/images/sidebar/eth_light.svg',
  },
  BNB: {
    light: '/assets/images/sidebar/bnb_light.svg',
    dark: '/assets/images/sidebar/bnb_dark.svg',
  },
  POLYGON: {
    light: '/assets/images/sidebar/polygon_light.svg',
    dark: '/assets/images/sidebar/polygon_dark.svg',
  },
  SD: {
    light: '/assets/images/sidebar/sd_light.svg',
    dark: '/assets/images/sidebar/sd_dark.svg',
  },
  HEDERA: {
    light: '/assets/images/sidebar/hedera_light.svg',
    dark: '/assets/images/sidebar/hedera_dark.svg',
  },
  FANTOM: {
    light: '/assets/images/sidebar/fantom_light.svg',
    dark: '/assets/images/sidebar/fantom_dark.svg',
  },
};

const Options = ({
  activeMenuKey,
  sidebarData,
  onClick,
  title,
  isEmbed,
}: OptionsProps) => {
  const { colorMode } = useColorMode();
  const pathname = usePathname();

  const TOKEN = getCurrentChain(pathname);

  const shouldDisplay = useCallback(
    (item: SubMenuType) => {
      if (item.display_if == null || item.display_if.length === 0) {
        return true;
      }

      return item.display_if.every((condition) => {
        if (condition.key === 'isEmbed') {
          const conditionValue = condition.value === 'true';
          return isEmbed === conditionValue;
        }

        return true;
      });
    },
    [isEmbed],
  );

  const composeUrl = useCallback((url: string) => {
    try {
      return template(url)({
        kelpdao_url: env.NEXT_PUBLIC_KELPDAO_URL,
      });
    } catch (err) {
      captureException(err, {
        contexts: {
          values: {
            url,
            kelpdao_url: env.NEXT_PUBLIC_KELPDAO_URL,
          },
        },
        tags: {
          [SENTRY_TAGS.TYPE]: SENTRY_TAGS_TYPE_VALUES.GENERAL,
          [SENTRY_TAGS.MODULE]: 'lodash-template',
          [SENTRY_TAGS.OPERATION]: 'composeUrl',
        },
      });
      return url;
    }
  }, []);

  return (
    <>
      {title ? (
        <Text
          mb="1rem"
          fontWeight="700"
          fontSize="10px"
          lineHeight="16px"
          letterSpacing="0.5px"
        >
          GOVERNANCE
        </Text>
      ) : null}

      {sidebarData ? (
        <Flex
          bg={{ base: 'backgroundPrimary', xl: 'backgroundSecondary' }}
          borderRadius="1rem"
          p="1rem 0.5rem 1.5rem 1rem"
          flexDirection="column"
        >
          <Box key={sidebarData.key}>
            <Flex alignItems="center" mb="1rem">
              <BaseImage
                src={
                  TOKEN &&
                  (IconMappings[TOKEN] ? IconMappings[TOKEN][colorMode] : '')
                }
                alt="chain"
                width={20}
                height={20}
              />

              <TextPrimary
                ml="0.5rem"
                fontSize="14px"
                fontWeight="600"
                lineHeight="20px"
              >
                {sidebarData.name}
              </TextPrimary>
            </Flex>
            {sidebarData?.subMenus &&
              sidebarData.subMenus.map((data: SubMenuType) =>
                shouldDisplay(data) ? (
                  <Box
                    // isExternal={!data.is_internal_link}
                    cursor={data.is_disabled ? 'not-allowed' : 'pointer'}
                    key={data.key}
                    _hover={{
                      textDecoration: 'none',
                    }}
                    onClick={() => onClick(data.key)}
                    bg={
                      activeMenuKey === data.key
                        ? 'backgroundPrimary'
                        : 'inherit'
                    }
                    ml="1.5rem"
                    p="8px 0"
                    borderRadius="8px"
                  >
                    {/* TODO isExternal code */}
                    {data.is_internal_link ? (
                      <Link link={composeUrl(data.link_url ?? '')}>
                        <Flex gap={1.5} alignItems="center">
                          <Text
                            ml="0.5rem"
                            color={
                              activeMenuKey === data.key
                                ? 'textPrimary'
                                : 'textSecondary'
                            }
                            fontSize="14px"
                            fontWeight="600"
                            lineHeight="20px"
                          >
                            {data.name}
                          </Text>
                        </Flex>
                      </Link>
                    ) : (
                      <a
                        href={composeUrl(data.link_url ?? '')}
                        target="_blank"
                        rel="noreferrer"
                        key={data.key}
                      >
                        <Flex>
                          <Text
                            ml="0.5rem"
                            color={
                              activeMenuKey === data.key
                                ? 'textPrimary'
                                : 'textSecondary'
                            }
                            fontSize="14px"
                            fontWeight="600"
                            lineHeight="20px"
                          >
                            {data.name}
                          </Text>
                        </Flex>
                      </a>
                    )}
                  </Box>
                ) : null,
              )}
          </Box>
        </Flex>
      ) : null}
    </>
  );
};

export default Options;
